import { useEffect, useMemo, useState } from "react";
import useDatasets from "datasets/hooks/useDatasets";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { AlertCircle, Search } from "lucide-react";
import { mutate } from "swr";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { mutateDatasetList } from "~/services/catalog";
import { projectDatasetsPath, setProjectDatasets } from "~/services/projects";

interface ProjectDatasetsDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachedIds: string[];
}

export const ProjectDatasetsDialog = ({
  projectId,
  open,
  onOpenChange,
  attachedIds,
}: ProjectDatasetsDialogProps) => {
  // `useDatasets` rather than `catalogDatasetsListUrl`: the search below needs full descriptions,
  // and this key is the one the Datasets page and Home have usually cached already.
  const { data, error, isLoading } = useDatasets({ enabled: open });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
    }
  }, [open]);

  const attachedSet = useMemo(() => new Set(attachedIds), [attachedIds]);

  const available = useMemo(
    () =>
      (data ?? [])
        .filter((d) => !attachedSet.has(d.id))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [data, attachedSet]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return available;
    }
    return available.filter(
      (d) => d.title.toLowerCase().includes(q) || (d.description ?? "").toLowerCase().includes(q)
    );
  }, [available, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { execute, isPending } = useAsyncMutation(
    (ids: string[]) => setProjectDatasets(projectId, ids),
    {
      errorMessage: "Failed to add datasets",
      onSuccess: async () => {
        // The project resolves attached ids against `catalogDatasetsListUrl`, which may predate a
        // dataset this dialog offered from the fresher `useDatasets` list.
        await Promise.all([mutate(projectDatasetsPath(projectId)), mutateDatasetList()]);
        onOpenChange(false);
      },
    }
  );

  const handleSave = () => {
    const newIds = [...attachedIds, ...Array.from(selected)];
    void execute(newIds);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Datasets"
      description="Select datasets to add to this project."
      contentClassName="sm:max-w-[640px]"
      onPrimary={handleSave}
      isPending={isPending}
      primaryLabel="Add"
      primaryPendingLabel="Adding…"
      primaryDisabled={selected.size === 0}
    >
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search datasets..."
          aria-label="Search datasets"
          className="pl-9"
        />
      </div>

      <div className="-mx-2 flex h-[50vh] flex-col overflow-y-auto">
        {isLoading && (
          <div role="status" className="flex flex-col gap-2 px-2 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-destructive"
          >
            <AlertCircle className="size-6" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load datasets</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {search ? "No datasets match your search." : "No more datasets to add."}
          </p>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <ul className="flex flex-col" role="list">
            {filtered.map((dataset) => (
              <DatasetRow
                key={dataset.id}
                dataset={dataset}
                checked={selected.has(dataset.id)}
                onToggle={() => toggle(dataset.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </FormDialog>
  );
};

interface DatasetRowProps {
  dataset: DatasetListItem;
  checked: boolean;
  onToggle: () => void;
}

const DatasetRow = ({ dataset, checked, onToggle }: DatasetRowProps) => {
  const checkboxId = `dataset-row-${dataset.id}`;
  return (
    <li>
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors",
          "hover:bg-accent/40"
        )}
      >
        <DatasetIcon dataset={dataset} size={20} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{dataset.title}</p>
          <p className="truncate text-xs text-muted-foreground">
            {dataset.description || "No description"}
          </p>
        </div>
        <Checkbox id={checkboxId} checked={checked} onCheckedChange={onToggle} />
      </label>
    </li>
  );
};
